import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Switch
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useSnackbar } from 'notistack';
import api, { CHECKINS, TRADE } from '../../infrastructure/config/api';

const DEFAULT_ASSETS = ['XAUUSDT', 'XAGUSDT'];

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
}

function parseDecimalInput(value) {
  if (value === undefined || value === null) return NaN;
  const normalized = String(value).trim().replace(',', '.');
  if (!normalized) return NaN;
  return Number(normalized);
}

function getDateStringInTimezone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(date);
}

function getMonthString(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit'
  });
  return formatter.format(date);
}

function getDaysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function getMonthMatrix(monthValue) {
  const [yearStr, monthStr] = monthValue.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const daysInMonth = getDaysInMonth(year, monthIndex);
  const firstDay = new Date(year, monthIndex, 1).getDay(); // 0 Sunday
  const offset = (firstDay + 6) % 7; // Monday start

  const cells = [];
  for (let i = 0; i < offset; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const dayString = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push(dayString);
  }
  return cells;
}

const CheckinPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const allowedRoles = new Set(['admin', 'holder']);
  if (currentUser.role && !allowedRoles.has(currentUser.role)) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Access denied
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Daily check-in is available for admin and holder accounts only.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }
  const [month, setMonth] = useState(getMonthString(new Date(), 'Asia/Ho_Chi_Minh'));
  const [timezone, setTimezone] = useState('Asia/Ho_Chi_Minh');
  const [summary, setSummary] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [amount, setAmount] = useState('1.00');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [tradeSnapshots, setTradeSnapshots] = useState({});
  const [historyPage, setHistoryPage] = useState(1);
  const [assets, setAssets] = useState([]);
  const [assetAmountDrafts, setAssetAmountDrafts] = useState({});
  const [selectedAsset, setSelectedAsset] = useState(DEFAULT_ASSETS[0]);
  const [assetUpdating, setAssetUpdating] = useState(false);

  const todayString = getDateStringInTimezone(new Date(), timezone);

  const checkinMap = useMemo(() => {
    const map = new Map();
    checkins.forEach((item) => {
      const dateKey = String(item.date).slice(0, 10);
      map.set(dateKey, item);
    });
    return map;
  }, [checkins]);

  const calendarCells = useMemo(() => getMonthMatrix(month), [month]);


  const fetchAssets = async () => {
    try {
      const response = await api.get(CHECKINS.ASSETS, {
        params: { symbols: DEFAULT_ASSETS.join(',') }
      });
      const data = response.data?.data || [];
      setAssets(data);
      setAssetAmountDrafts(() => {
        const next = {};
        data.forEach((item) => {
          next[item.symbol] = String(item.daily_amount_usd ?? '');
        });
        return next;
      });
      if (data.length && !data.find((item) => item.symbol === selectedAsset)) {
        setSelectedAsset(data[0].symbol);
      }
    } catch (error) {
      enqueueSnackbar('Failed to load asset configs', { variant: 'error' });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(CHECKINS.LIST, {
        params: { month, asset: selectedAsset }
      });
      const data = response.data?.data || {};
      const resolvedTz = data.timezone || 'Asia/Ho_Chi_Minh';
      setTimezone(resolvedTz);
      setCheckins(data.checkins || []);
      setSummary(data.summary || null);
    } catch (error) {
      enqueueSnackbar('Failed to load check-ins', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeSnapshot = async () => {
    try {
      const response = await api.get(TRADE.RUNNING);
      const trades = response.data?.data || [];
      const map = {};
      trades.forEach((trade) => {
        const symbol = String(trade.symbol || '').toUpperCase();
        map[symbol] = trade;
      });
      setTradeSnapshots(map);
    } catch (error) {
      setTradeSnapshots({});
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);
  useEffect(() => {
    fetchData();
    fetchTradeSnapshot();
  }, [month, selectedAsset]);

  useEffect(() => {
    setHistoryPage(1);
  }, [month, checkins.length]);

  const handleDayClick = (dayString) => {
    if (!dayString) return;
    if (dayString > todayString) {
      enqueueSnackbar('Cannot check-in for future date', { variant: 'warning' });
      return;
    }
    const existing = checkinMap.get(dayString);
    setSelectedDate(dayString);
    if (existing) {
      setSelectedCheckin(existing);
      setNote(existing.note || '');
      setAmount(String(existing.amount || '1.00'));
      setDetailOpen(true);
    } else {
      const defaultAmount = selectedAssetConfig ? String(selectedAssetConfig.daily_amount_usd || '1.00') : '1.00';
      setAmount(defaultAmount);
      setNote('');
      setConfirmOpen(true);
    }
  };

  const handleCreateCheckin = async () => {
    if (selectedAssetConfig && !selectedAssetConfig.enabled) {
      enqueueSnackbar('Asset is disabled', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const response = await api.post(CHECKINS.CREATE, {
        asset: selectedAsset,
        amount: parseDecimalInput(amount),
        note,
        date: selectedDate,
        timezone
      });
      const created = response.data?.data;
      setConfirmOpen(false);
      if (created) {
        setCheckins((prev) => {
          const next = prev.filter((item) => String(item.date).slice(0, 10) !== selectedDate);
          return [created, ...next];
        });
        await fetchData();
        enqueueSnackbar('Check-in recorded', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to check-in', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCheckin = async () => {
    if (!selectedCheckin) return;
    setSaving(true);
    try {
      const response = await api.patch(CHECKINS.UPDATE(selectedCheckin.id), {
        note,
        amount: parseDecimalInput(amount)
      });
      const updated = response.data?.data;
      setDetailOpen(false);
      if (updated) {
        setCheckins((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        await fetchData();
        enqueueSnackbar('Check-in updated', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Update failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAssetUpdate = async (symbol, payload) => {
    setAssetUpdating(true);
    try {
      const response = await api.put(CHECKINS.ASSET_UPDATE(symbol), payload);
      const updated = response.data?.data;
      if (updated) {
        setAssets((prev) => prev.map((item) => (item.symbol === updated.symbol ? updated : item)));
        setAssetAmountDrafts((prev) => ({
          ...prev,
          [updated.symbol]: String(updated.daily_amount_usd ?? '')
        }));
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to update asset', { variant: 'error' });
    } finally {
      setAssetUpdating(false);
    }
  };

  const historyItems = useMemo(() => {
    return [...checkins].sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [checkins]);
  const historyPageSize = 10;
  const historyTotalPages = Math.max(1, Math.ceil(historyItems.length / historyPageSize));
  const historySlice = historyItems.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);

  const selectedAssetConfig = assets.find((item) => item.symbol === selectedAsset) || null;
  const [monthYear, monthMonth] = month.split('-').map(Number);
  const daysInMonth = Number.isFinite(monthYear) && Number.isFinite(monthMonth)
    ? getDaysInMonth(monthYear, monthMonth - 1)
    : 30;

  const assetMonthlyEstimate = (asset) => {
    return Number(asset?.daily_amount_usd || 0) * daysInMonth;
  };

  const handleQuickCheckin = async (asset) => {
    if (!asset || !asset.enabled) {
      enqueueSnackbar('Asset is disabled', { variant: 'warning' });
      return;
    }
    try {
      await api.post(CHECKINS.CREATE, {
        asset: asset.symbol,
        amount: Number(asset.daily_amount_usd),
        note: 'manual-checkin',
        timezone
      });
      await fetchData();
      enqueueSnackbar('Check-in recorded', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Failed to check-in', { variant: 'error' });
    }
  };

  const handleAmountDraftChange = (symbol, value) => {
    setAssetAmountDrafts((prev) => ({ ...prev, [symbol]: value }));
  };

  const handleSaveAssetAmount = async (symbol) => {
    const raw = assetAmountDrafts[symbol];
    const parsed = parseDecimalInput(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      enqueueSnackbar('Daily investment phải lớn hơn 0', { variant: 'warning' });
      return;
    }
    await handleAssetUpdate(symbol, { daily_amount_usd: parsed });
    enqueueSnackbar(`Đã cập nhật ${symbol}: $${formatNumber(parsed)}`, { variant: 'success' });
  };

  const handleMonthShift = (delta) => {
    const [yearStr, monthStr] = month.split('-');
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const next = new Date(Date.UTC(year, monthIndex + delta, 1));
    const nextMonth = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`;
    setMonth(nextMonth);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Stack>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Daily Investment Check-in
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Asset: {selectedAsset} · Timezone: {timezone}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleMonthShift(-1)}
              sx={{
                minWidth: 44,
                height: 40,
                borderRadius: 2,
                px: 1.5,
                fontWeight: 600,
                letterSpacing: '0.04em'
              }}
            >
              ‹
            </Button>
            <TextField
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              size="small"
              sx={{
                minWidth: 180,
                '& .MuiInputBase-root': {
                  height: 40,
                  borderRadius: 2,
                  fontWeight: 600
                },
                '& .MuiInputBase-input': {
                  fontSize: '1rem',
                  letterSpacing: '0.02em'
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleMonthShift(1)}
              sx={{
                minWidth: 44,
                height: 40,
                borderRadius: 2,
                px: 1.5,
                fontWeight: 600,
                letterSpacing: '0.04em'
              }}
            >
              ›
            </Button>
          </Stack>
        </Stack>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {assets.map((asset) => (
                  <Button
                    key={asset.symbol}
                    size="small"
                    variant={asset.symbol === selectedAsset ? 'contained' : 'outlined'}
                    onClick={() => setSelectedAsset(asset.symbol)}
                    sx={{ borderRadius: 2 }}
                  >
                    {asset.symbol}
                  </Button>
                ))}
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                {assets.map((asset) => {
                  const tradeSnapshot = tradeSnapshots[asset.symbol];
                  const isActive = asset.enabled;
                  const checkedToday = asset.symbol === selectedAsset && summary?.checkedToday;
                  const statusLabel = isActive ? 'Active' : 'Paused';
                  return (
                    <Card
                      key={asset.symbol}
                      onClick={() => setSelectedAsset(asset.symbol)}
                      sx={{
                        borderRadius: 2,
                        border: asset.symbol === selectedAsset ? '1px solid rgba(130, 200, 120, 0.6)' : '1px solid rgba(255,255,255,0.08)',
                        background: 'linear-gradient(180deg, rgba(32,32,32,0.9) 0%, rgba(24,24,24,0.95) 100%)'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {asset.symbol} · {asset.symbol === 'XAUUSDT' ? 'Gold' : asset.symbol === 'XAGUSDT' ? 'Silver' : 'Asset'}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" color="text.secondary">Status:</Typography>
                              <Chip
                                size="small"
                                label={statusLabel}
                                icon={isActive ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />}
                                sx={{
                                  backgroundColor: isActive ? 'rgba(56, 211, 159, 0.18)' : 'rgba(255,255,255,0.08)',
                                  color: isActive ? 'success.main' : 'text.secondary'
                                }}
                              />
                              <Switch
                                size="small"
                                checked={isActive}
                                onChange={(event) => handleAssetUpdate(asset.symbol, { enabled: event.target.checked })}
                                disabled={assetUpdating}
                              />
                            </Stack>
                          </Stack>
                          <Chip
                            size="small"
                            label={`${tradeSnapshot?.strategy?.name || 'Default'} → ${tradeSnapshot?.strategy?.version || 'v1'}`}
                            sx={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }}
                          />
                        </Stack>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mt: 2 }}>
                          <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                            <Typography variant="caption" color="text.secondary">Daily investment</Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={assetAmountDrafts[asset.symbol] ?? ''}
                                onChange={(e) => {
                                  handleAmountDraftChange(asset.symbol, e.target.value);
                                }}
                                onBlur={() => handleSaveAssetAmount(asset.symbol)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveAssetAmount(asset.symbol);
                                  }
                                }}
                                inputProps={{ step: '0.1', min: '0' }}
                                sx={{ width: 120 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleSaveAssetAmount(asset.symbol)}
                                disabled={assetUpdating}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                          <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                            <Typography variant="caption" color="text.secondary">PnL</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, color: (tradeSnapshot?.totalPnl || 0) >= 0 ? 'success.main' : 'error.main' }}>
                              {tradeSnapshot?.totalPnl !== undefined ? `${formatNumber(tradeSnapshot.totalPnl)} $` : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                            <Typography variant="caption" color="text.secondary">Total Size</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {tradeSnapshot?.totalSize ? `${formatNumber(tradeSnapshot.totalSize)} USDT` : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                            <Typography variant="caption" color="text.secondary">Price</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {tradeSnapshot?.currentPrice ? formatNumber(tradeSnapshot.currentPrice) : '—'}
                            </Typography>
                          </Box>
                        </Box>

                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Monthly estimate: ~ ${formatNumber(assetMonthlyEstimate(asset))}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                              <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={asset.mode}
                                onChange={(_, value) => value && handleAssetUpdate(asset.symbol, { mode: value })}
                              >
                                <ToggleButton value="AUTO">Auto</ToggleButton>
                                <ToggleButton value="MANUAL">Manual</ToggleButton>
                              </ToggleButtonGroup>
                              <Button
                                size="small"
                                variant="contained"
                                color={isActive ? 'success' : 'inherit'}
                                onClick={() => (isActive ? handleQuickCheckin(asset) : handleAssetUpdate(asset.symbol, { enabled: true }))}
                              >
                                {isActive ? (checkedToday ? 'Checked in today' : 'Check in today') : 'Enable investing'}
                              </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: 2,
            alignItems: 'start'
          }}
        >
          <Card>
            <CardContent>
              {loading || !summary ? (
                <Stack spacing={1}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="text" width="50%" />
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Summary
                  </Typography>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Streak</Typography>
                    <Typography fontWeight={600}>{summary.currentStreak} days</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Total invested</Typography>
                    <Typography fontWeight={600}>${formatNumber(summary.totalInvested)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>This month</Typography>
                    <Typography fontWeight={600}>${formatNumber(summary.monthInvested)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Last check-in</Typography>
                    <Typography fontWeight={600}>
                      {summary.lastCheckinAt ? new Date(summary.lastCheckinAt).toLocaleString() : '—'}
                    </Typography>
                  </Stack>
                  <Chip
                    color={summary.checkedToday ? 'success' : 'default'}
                    label={summary.checkedToday ? 'Checked in today' : 'Not checked today'}
                    size="small"
                  />
                </Stack>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  Monthly Calendar
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
                  {dayLabels.map((label) => (
                    <Box
                      key={label}
                      sx={{
                        textAlign: 'center',
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: 'rgba(255,255,255,0.03)'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                        {label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    gap: 1
                  }}
                >
                  {calendarCells.map((dayString, index) => {
                    const isToday = dayString === todayString;
                    const checked = dayString && checkinMap.has(dayString);
                    const isEmpty = !dayString;
                    return (
                      <Box
                        key={`${dayString || 'empty'}-${index}`}
                        onClick={() => handleDayClick(dayString)}
                        sx={{
                          minHeight: { xs: 44, sm: 52 },
                          borderRadius: 1.25,
                          px: 1,
                          py: 0.75,
                          backgroundColor: isEmpty
                            ? 'transparent'
                            : checked
                              ? 'rgba(56, 211, 159, 0.18)'
                              : 'rgba(255,255,255,0.04)',
                          border: isEmpty ? '1px solid transparent' : '1px solid rgba(255,255,255,0.08)',
                          boxShadow: checked ? '0 0 0 1px rgba(56, 211, 159, 0.4) inset' : 'none',
                          cursor: isEmpty ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease',
                          ...(isToday && !checked
                            ? { border: '1px solid rgba(98, 208, 255, 0.6)', boxShadow: '0 0 0 1px rgba(98,208,255,0.25) inset' }
                            : null),
                          '&:hover': !isEmpty
                            ? { backgroundColor: 'rgba(255,255,255,0.08)' }
                            : null
                        }}
                      >
                        <Typography variant="body2">
                          {dayString ? Number(dayString.slice(-2)) : ''}
                        </Typography>
                        {checked && <CheckCircleIcon fontSize="small" color="success" />}
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="text.secondary">
                  History
                </Typography>
                <Chip
                  size="small"
                  label={`${historyItems.length} entries`}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'text.secondary' }}
                />
              </Stack>
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((idx) => (
                    <Skeleton key={idx} variant="rounded" height={48} />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {historyItems.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No check-ins yet.</Typography>
                  )}
                  {historySlice.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr 120px' },
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        borderRadius: 1.5,
                        p: 1.25,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.05)'
                      }}
                    >
                      <Stack spacing={0.3}>
                        <Typography variant="caption" color="text.secondary">
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {String(item.date).slice(0, 10)}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.3}>
                        <Typography variant="caption" color="text.secondary">
                          Note
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.note || '—'}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="caption" color="text.secondary">
                          Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ${formatNumber(item.amount)}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                  {historyItems.length > historyPageSize && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                      <Pagination
                        count={historyTotalPages}
                        page={historyPage}
                        onChange={(_, value) => setHistoryPage(value)}
                        size="small"
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          Check-in today?
          <IconButton onClick={() => setConfirmOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Check-in ${amount} for {selectedAsset} on {selectedDate || todayString}
            </Typography>
            <TextField
              label="Amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCheckin} disabled={saving}>
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          Already checked today
          <IconButton onClick={() => setDetailOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedCheckin?.date ? String(selectedCheckin.date).slice(0, 10) : selectedDate || todayString} · {selectedAsset}
            </Typography>
            <TextField
              label="Amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Edit note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleUpdateCheckin} disabled={saving}>
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CheckinPage;
